import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, PlusCircle, Loader2 } from "lucide-react";
import { ClassifiedModal } from "../components/classfields_components/ClassifiedModal";
import MainLayout from "../components/MainLayout";
import PropertyListing from "../components/classfields_components/PropertyListing";
import VehicleListing from "../components/classfields_components/VechileListing";
import ElectronicsListing from "../components/classfields_components/ElectronicListing";
import ClothingListing from "../components/classfields_components/ClothingListing";
import RoommateListing from "../components/classfields_components/RoommateListing";
import ApplianceListing from "../components/classfields_components/ApplianceListing";
import SubleaseListing from "../components/classfields_components/SubleaseListing";
import { useIsMobile } from "../hooks/use-mobile";
import {
  getInitialListings,
  getListing,
  getListingCategories,
} from "../services/classified";
import { successToast,errorToast } from '../utils/toasters'
import ClassifiedDetailsView from "../components/classfields_components/ClassifieldDetailsView";
import { isAuthenticated } from "../utils/helpers";
import { ClassifiedContent } from "../components/classfields_components/ClassifiedContent";

const ClassifiedPage = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [categoryData, setCategoryData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [isLoadingListings, setIsLoadingListings] = useState(true);
  const [isRefreshingListings, setIsRefreshingListings] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [listingsData, setListingsData] = useState([]);
  const [hasPendingData, setHasPendingData] = useState(false);
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const pendingData = localStorage.getItem("pendingClassifiedData");
    if (pendingData && isAuthenticated()) {
      setHasPendingData(true);
    }
  }, []);

  // Handle URL category parameter
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    if (categoryParam) {
      setActiveTab(categoryParam);
    }
  }, [searchParams]);

  // Fetch categories
  useEffect(() => {
    async function fetchCategories() {
      try {
        setIsLoadingCategories(true);
        const response = await getListingCategories();
        const responseMap = response.data.map((data) => ({
          value: data.name,
          label: data.name,
        }));
        const categoriesWithAll = [
          { value: "All", label: "All" },
          ...responseMap,
        ];
        setCategoryData(categoriesWithAll);
      } catch (error) {
        console.error("Error fetching categories:", error);
        errorToast("Error loading categories");
      } finally {
        setIsLoadingCategories(false);
      }
    }
    fetchCategories();
  }, []);

  // Fetch listings
  useEffect(() => {
    async function fetchInitialListings() {
      try {
        setIsLoadingListings(true);
        const response = await getInitialListings();
        setListingsData(response.data.data);
      } catch (error) {
        console.error("Error fetching listings:", error);
        errorToast("Error loading listings");
      } finally {
        setIsLoadingListings(false);
      }
    }
    fetchInitialListings();
  }, [activeTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setViewMode("list");
    setSelectedListing(null);
  };

  const handleListingClick = async (listing) => {
    setIsProcessing(true);
    try {
      const listingDetail = await getListing(listing.id);
      setSelectedListing(listingDetail.data);
      setViewMode("detail");
    } catch (error) {
      errorToast("Error while fetching product details");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToList = () => {
    setViewMode("list");
    setSelectedListing(null);
  };

  const handleItemCreated = async () => {
    try {
      setIsRefreshingListings(true);
      const response = await getInitialListings();
      setListingsData(response.data.data);
    } catch (error) {
      errorToast(
        "Failed to refresh listings. Please refresh the page to see your new item."
      );
      setTimeout(() => window.location.reload(), 2000);
    } finally {
      setIsRefreshingListings(false);
    }
  };

  const categoryComponentMap = {
    House: { component: PropertyListing, propName: "propertyItems" },
    Auto: { component: VehicleListing, propName: "vehicleItems" },
    Sublease: { component: SubleaseListing, propName: "subleaseItems" },
    Appliances: { component: ApplianceListing, propName: "applianceItems" },
    Electronics: {
      component: ElectronicsListing,
      propName: "electronicsItems",
    },
    Apparels: { component: ClothingListing, propName: "clothingItems" },
    Roommate: { component: RoommateListing, propName: "roommateItems" },
  };

  const CategoryTabsSkeleton = () => (
    <div className="flex space-x-4 mb-2 border-b border-gray-300 pb-2">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <div key={i} className="flex flex-col items-center animate-pulse">
          <div className="w-5 h-5 bg-gray-200 rounded mb-1"></div>
          <div className="h-3 bg-gray-200 rounded w-12"></div>
        </div>
      ))}
    </div>
  );

  return (
    <MainLayout rs={false}>
      <div className="relative font-afacad">
        {/* Refreshing overlay */}
        {isRefreshingListings && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center space-x-3 shadow-lg">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="text-gray-700 font-medium">
                Updating listings...
              </span>
            </div>
          </div>
        )}

        {/* Tabs Section - Only show in list view */}
        {viewMode === "list" && (
          <div className="flex flex-wrap justify-center md:justify-start mb-2 border-b border-gray-300 relative md:overflow-x-hidden">
            {isLoadingCategories ? (
              <CategoryTabsSkeleton />
            ) : (
              <div
                className="flex overflow-x-auto md:overflow-x-hidden"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {categoryData?.map((tab) => (
                  <button
                    key={tab.label.toLowerCase()}
                    className={`py-0 px-4 text-sm font-medium transition-all duration-300 border-b-2 ${
                      activeTab === tab.label
                        ? "border-gray-900 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-400 hover:text-gray-700"
                    }`}
                    onClick={() => handleTabChange(tab.label)}
                  >
                    <div className="flex flex-col items-center">
                      <img
                        src={`/images/${tab.label.toLowerCase()}.png`}
                        className="w-5 mb-1"
                        alt="Category Icon"
                      />
                      {tab.label}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Create button for larger screens */}
            <button
              onClick={() => setIsModalOpen(true)}
              className="hidden md:block absolute right-0 py-1.5 px-3 bg-black text-white rounded-lg top-1/2 -translate-y-1/2"
            >
              + Create Classfields
            </button>

            {/* Floating button for mobile */}
            {isMobile && !isModalOpen && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="fixed w-14 h-14 rounded-full bg-[#7B189F] flex items-center justify-center shadow-md border-none cursor-pointer"
                style={{
                  bottom: "4.5rem",
                  right: "2rem",
                  zIndex: 9999,
                }}
              >
                <PlusCircle size={32} color="white" />
              </button>
            )}
          </div>
        )}

        {/* Content rendering */}
        {viewMode === "list" ? (
          isLoadingListings ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : listingsData?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-gray-400 mb-4">
                <PlusCircle size={64} />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No listings found
              </h3>
              <p className="text-gray-500 text-center mb-4">
                Be the first to create a classified listing in this category
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-6 py-2 bg-[#7B189F] text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Create First Listing
              </button>
            </div>
          ) : (
            listingsData?.map((data) => {
              const title = data.category.title;
              const config = categoryComponentMap[title];

              if (!config || (activeTab !== "All" && activeTab !== title))
                return null;

              const Component = config.component;
              const props = {
                isDouble: activeTab === "All" ? false : true,
                [config.propName]: data.category.products || [],
                onItemClick: handleListingClick,
              };

              return (
                <div key={title} className="mb-6 pl-4 pt-2">
                  <div className="flex justify-between">
                    <h2 className="text-xl font-bold font-fraunces pb-4">
                      {title}
                    </h2>
                    <div className="flex items-center space-x-2 font-bold text-gray-600 text-xs mr-2">
                      {data.category.products.length > 5 && (
                        <>
                          <span>See more</span>
                          <ArrowRight
                            className="text-sm text-gray-600"
                            width={10}
                            height={10}
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <Component
                    initialImage={"/image/default-classified-ad.webp"}
                    imgClick={() => setIsModalOpen(true)}
                    {...props}
                  />
                </div>
              );
            })
          )
        ) : (
          <ClassifiedDetailsView
            listing={selectedListing}
            category={selectedListing?.category_name || ""}
            onBack={handleBackToList}
          />
        )}

        {/* Regular modal for manual creation */}
        <ClassifiedModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onItemCreated={handleItemCreated}
        />

        {/* Hidden ClassifiedContent for handling pending data without showing modal */}
        {hasPendingData && !isModalOpen && (
          <div style={{ display: "none" }}>
            <ClassifiedContent
              onClose={() => setHasPendingData(false)}
              onItemCreated={handleItemCreated}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ClassifiedPage;
